import logger from '../../log';
import { GatherArgs } from '../interfaces';
import { resolveOnTimeout } from '../../common/utils/promise';

const log = logger.getLogger(__filename);

class LocalPeer {
  rtcConfiguration: RTCConfiguration;
  dataChannelInit: RTCDataChannelInit;
  candidates: RTCIceCandidate[];

  p1: RTCPeerConnection;
  d1: RTCDataChannel;

  p2: RTCPeerConnection;
  d2: RTCDataChannel | undefined;
  timeout: number;

  /**
   * Create a new instance of LocalPeer. Connection care created by the constructor.
   * @param rtcConfiguration - RTCConfiguration for the RTCPeerConnection
   * @param dataChannelOptions - RTCDataChannelInit config for RTCDataChannel
   */
  constructor(
    timeout: number,
    rtcConfiguration?: RTCConfiguration,
    dataChannelOptions?: RTCDataChannelInit
  ) {
    this.rtcConfiguration = rtcConfiguration ?? {};
    this.dataChannelInit = dataChannelOptions ?? {};
    this.timeout = timeout;
    this.candidates = [];

    this.p1 = new RTCPeerConnection(this.rtcConfiguration);
    this.d1 = this.p1.createDataChannel('localChannel', this.dataChannelInit);
    this.p2 = new RTCPeerConnection(this.rtcConfiguration);
    this.p2.ondatachannel = (e): void => {
      this.d2 = e.channel;
    };
  }

  async start(): Promise<void> {
    const promise1 = new Promise<void>(resolve => {
      this.p1.onicecandidate = (e): void => {
        if (e.candidate == null) {
          resolve();
        } else {
          this.candidates.push(e.candidate);
        }
      };
    });

    const promise2 = new Promise<void>(resolve => {
      this.p2.onicecandidate = (e): void => {
        if (e.candidate == null) {
          resolve();
        }
        // SRV-849 We don't need to capture ice candidates from both sides.
      };
    });

    this.p1
      .createOffer()
      .then(offer => this.p1.setLocalDescription(offer))
      .then(() => {
        if (this.p1.localDescription) {
          return this.p2.setRemoteDescription(this.p1.localDescription);
        }
      })
      .then(() => this.p2.createAnswer())
      .then(answer => this.p2.setLocalDescription(answer))
      .then(() => {
        if (this.p2.localDescription) {
          return this.p1.setRemoteDescription(this.p2.localDescription);
        }
      })
      .catch(e => {
        log.trace('Error creating datachannel in LocalPeer', e);
      });

    await Promise.race([
      Promise.all([promise1, promise2]),
      resolveOnTimeout(this.timeout)
    ]);
  }

  /**
   * Close relevant resources.
   */
  close(): void {
    this.p1.close();
    if (this.d1) {
      this.d1.close();
    }
    this.p2.close();
    if (this.d2) {
      this.d2.close();
    }
  }
}

const getIceCandidatesFromWebrtc = async (
  opts?: GatherArgs
): Promise<RTCIceCandidate[]> => {
  const { rtcConfiguration, dataChannelOptions, timeout } = opts ?? {};
  try {
    const to = timeout && timeout >= 1000 ? timeout : 4000;
    const localPeer = new LocalPeer(
      to,
      rtcConfiguration ?? {},
      dataChannelOptions
    );
    await localPeer.start();
    const data = [...localPeer.candidates];
    // shut down the localPeer instance
    localPeer.close();
    return data;
  } catch (e) {
    log.warn('Failed to gather ice candidates from local peer connections', e);
    return [];
  }
};

export default getIceCandidatesFromWebrtc;
